/**
 * CallLifecycleController — thin seam between the Redux middleware and the
 * low-level `AsrConnectionManager`.
 *
 * Purpose
 * -------
 * Today `CallManager.ts` (middleware) talks directly to `AsrConnectionManager`
 * and also owns local state (a 3-second ping interval, an idle-reload
 * scheduler) that duplicates what ACM or its invariants already provide.
 *
 * The controller absorbs those responsibilities so the middleware can shrink
 * to a pure action → intent adapter. The eventual goal is to move the full
 * call-lifecycle state machine (see docs/call-lifecycle.md) into this class.
 *
 * What this file owns TODAY (step 2 of the refactor):
 *   - Creation and ownership of `AsrConnectionManager`.
 *   - The 3-second active-call ping (previously `ping` + `_intervalPing` in
 *     `CallManager.ts`).
 *   - `scheduleReloadOnIdle` (previously a closure inside the middleware).
 *
 * What it does NOT own yet (left for follow-up steps):
 *   - Explicit FSM state tracking (planned: step 3).
 *   - The keyphrase timeout check currently polled from
 *     `AUDIO_CHUNK_RECEIVED` (planned: step 3).
 *   - Direct subscription to `AsrConnection` events — for now ACM continues
 *     to dispatch actions itself.
 *
 * The surface is an *intent-named* API (`onCallStarted`, `hold`, `emitCallEnd`,
 * …) so that the middleware reads like a script, not a marshalling layer.
 */

import AsrConnectionManager from './AsrConnectionManager';
import { reload } from '../store/actions/backendCommon';
import { EXPLICIT_MODE_RELOAD_TIMEOUT } from '../constants/backend';
import type { UnknownDispatch } from '../store/types/backend';

/**
 * Interval between pings while a call is active. Lives in the controller
 * because it is a call-level concern, not a transport-level concern.
 */
const ACTIVE_CALL_PING_INTERVAL_MSEC = 3000;

type StartConfig = Parameters<AsrConnectionManager['start']>[0];
type StartRecognitionConfig = Parameters<AsrConnectionManager['start']>[1];
type StopArgs = Parameters<AsrConnectionManager['stop']>[0];
type EmitCallStartArgs = Parameters<AsrConnectionManager['emitCallStartEvent']>[0];
type EmitCallEndArgs = Parameters<AsrConnectionManager['emitCallEndEvent']>[0];
type ForwardedEventArgs = Parameters<AsrConnectionManager['forwardEvent']>[0];
type AudioChunkArgs = Parameters<AsrConnectionManager['enqueueChunks']>[0]['audioChunk'];

export interface CallLifecycleControllerOptions {
    dispatch: UnknownDispatch;
}

export default class CallLifecycleController {
    private readonly _dispatch: UnknownDispatch;
    private readonly _asr: AsrConnectionManager;
    private _pingTimer: ReturnType<typeof setInterval> | null = null;

    constructor({ dispatch }: CallLifecycleControllerOptions) {
        this._dispatch = dispatch;
        this._asr = new AsrConnectionManager({ dispatch });
    }

    // ---- Detection session lifecycle ------------------------------------------------

    startDetection(config: StartConfig, recognitionConfig: StartRecognitionConfig) {
        this._asr.start(config, recognitionConfig);
    }

    stopDetection(args: StopArgs) {
        this._stopPing();
        this._asr.stop(args);
    }

    /**
     * Schedule a reload if the detection session stays idle (no new call)
     * for `EXPLICIT_MODE_RELOAD_TIMEOUT`. Previously a free function inside
     * `CallManager.ts`.
     *
     * The check is evaluated against the controller's own notion of whether
     * a call is active, which mirrors ACM's `isCallStarted()`.
     */
    scheduleReloadOnIdle() {
        setTimeout(() => {
            if (this.isCallActive()) {
                return;
            }
            this._dispatch(reload({ reason: 'CALL_ENDED', isImmediate: true, log: false }));
        }, EXPLICIT_MODE_RELOAD_TIMEOUT);
    }

    // ---- Call-level events ----------------------------------------------------------

    /**
     * Middleware handler for `CALL_STARTED` — wires up the recognition config,
     * primes the audio buffers, kicks the processing loop and turns on the
     * active-call ping.
     */
    onCallStarted(recognitionConfig: StartRecognitionConfig) {
        this._asr.startCall(recognitionConfig);
        this._asr.markAllUnSend();
        this._asr.processChunk();
        this._startPing();
    }

    /**
     * Middleware handler for `CALL_ENDED` — tears down the active-call ping.
     * The actual `stop()` call happens via `stopDetection()` in the current
     * design (mode branching is still in the middleware); once the FSM moves
     * here, this method will own the end transition fully.
     */
    onCallEnded() {
        this._stopPing();
    }

    emitCallStart(args: EmitCallStartArgs) {
        this._asr.emitCallStartEvent(args);
    }

    emitCallEnd(args: EmitCallEndArgs) {
        this._asr.emitCallEndEvent(args);
    }

    hold() {
        this._asr.emitCallHeld();
    }

    unhold() {
        this._asr.emitCallUnheld();
    }

    /**
     * Middleware handler for `CALL_HOLDED` (the confirmation, after the
     * `hold()` intent). Resets audio buffers without flushing.
     */
    onHolded() {
        this._asr.resetBuffer(false);
    }

    isCallActive(): boolean {
        return this._asr.isCallStarted();
    }

    // ---- Mode selection -------------------------------------------------------------

    setMode(mode: number, sendChannelControlRequest?: boolean, forgetAudioAndTranscript?: boolean) {
        this._asr.setCallMode(mode, sendChannelControlRequest, forgetAudioAndTranscript);
    }

    forgetAudio() {
        this._asr.sendForgetAudio();
    }

    resetMode() {
        this._asr.resetCallMode();
    }

    // ---- Audio pipeline -------------------------------------------------------------

    processAudioChunk(chunk: AudioChunkArgs) {
        this._asr.enqueueChunks({ audioChunk: chunk });
        this._asr.processChunk();
    }

    // ---- Forwarded events / pings ---------------------------------------------------

    forwardEvent(event: ForwardedEventArgs) {
        this._asr.forwardEvent(event);
    }

    /**
     * Low-level ping sent while on hold. Unlike the active-call ping this one
     * is fired per action (ASR_PING) by the middleware, not on an interval.
     */
    sendPing() {
        this._asr.sendPing();
    }

    utcPing() {
        this._asr.utcPing();
    }

    // ---- Config flags ---------------------------------------------------------------

    setRecorderDebugFlag(flag: boolean) {
        this._asr.setEnableRecorderDebugFlag(flag);
    }

    setVoiceEnergyDetectionFlag(flag: boolean) {
        this._asr.setVoiceEnergyDetectionFlag(flag);
    }

    // ---- Internals ------------------------------------------------------------------

    private _startPing() {
        this._stopPing();
        this._pingTimer = setInterval(() => {
            this._asr.sendPing();
        }, ACTIVE_CALL_PING_INTERVAL_MSEC);
    }

    private _stopPing() {
        if (this._pingTimer !== null) {
            clearInterval(this._pingTimer);
            this._pingTimer = null;
        }
    }
}
